import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ArrowLeft, MessageCircle, User, Mail, Trash2, Clock, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export default function AdminChatHistory() {
  const navigate = useNavigate();
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedConversation, setSelectedConversation] = useState(null);

  const getAuth = () => {
    const auth = localStorage.getItem("adminAuth");
    if (!auth) {
      navigate("/admin");
      return null;
    }
    return { Authorization: `Basic ${auth}` };
  };

  useEffect(() => {
    fetchConversations();
  }, []);

  const fetchConversations = async () => {
    const headers = getAuth();
    if (!headers) return;

    try {
      const response = await axios.get(`${API}/admin/chatbot/conversations`, { headers });
      setConversations(response.data);
    } catch (error) {
      toast.error("Erreur lors du chargement des conversations");
    } finally {
      setLoading(false);
    }
  };

  const deleteConversation = async (sessionId) => {
    const headers = getAuth();
    if (!headers) return;

    if (!window.confirm("Supprimer cette conversation ?")) return;

    try {
      await axios.delete(`${API}/admin/chatbot/conversations/${sessionId}`, { headers });
      toast.success("Conversation supprimée");
      fetchConversations();
      if (selectedConversation?.session_id === sessionId) {
        setSelectedConversation(null);
      }
    } catch (error) {
      toast.error("Erreur lors de la suppression");
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    const date = new Date(dateStr);
    return date.toLocaleDateString("fr-FR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-pink-50 to-white p-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <Button variant="ghost" onClick={() => navigate("/admin/dashboard")} data-testid="chat-history-back-btn">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-pink-800 flex items-center gap-2">
              <MessageCircle className="w-6 h-6" />
              Historique IA Sakura
            </h1>
            <p className="text-slate-500 text-sm">Conversations avec le chatbot</p>
          </div>
        </div>

        {/* Conversations List */}
        <Card className="border-pink-200">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg">
              {conversations.length} conversation(s)
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <p className="text-center text-slate-500 py-4">Chargement...</p>
            ) : conversations.length === 0 ? (
              <p className="text-center text-slate-500 py-4">Aucune conversation enregistrée</p>
            ) : (
              <div className="space-y-2">
                {conversations.map((conv) => (
                  <div
                    key={conv.session_id}
                    className="flex items-center justify-between p-3 rounded-lg border hover:bg-pink-50 transition-colors cursor-pointer"
                    onClick={() => setSelectedConversation(conv)}
                    data-testid={`chat-conv-${conv.session_id}`}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        {conv.customer_email ? (
                          <span className="flex items-center gap-1 text-sm font-medium text-blue-600">
                            <Mail className="w-4 h-4" />
                            {conv.customer_email}
                          </span>
                        ) : (
                          <span className="flex items-center gap-1 text-sm text-slate-500">
                            <User className="w-4 h-4" />
                            Visiteur anonyme
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-3 text-xs text-slate-500">
                        <span className="flex items-center gap-1">
                          <MessageCircle className="w-3 h-3" />
                          {conv.messages?.length || 0} message(s)
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {formatDate(conv.updated_at)}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={(e) => {
                          e.stopPropagation();
                          deleteConversation(conv.session_id);
                        }}
                        className="text-red-500 hover:text-red-700 hover:bg-red-50"
                        data-testid={`chat-conv-delete-${conv.session_id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                      <ChevronRight className="w-5 h-5 text-slate-400" />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Conversation Detail Dialog */}
        {selectedConversation && (
          <Dialog open={!!selectedConversation} onOpenChange={() => setSelectedConversation(null)}>
            <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <MessageCircle className="w-5 h-5 text-pink-600" />
                  Conversation
                  {selectedConversation.customer_email && (
                    <span className="text-sm font-normal text-blue-600 ml-2">
                      ({selectedConversation.customer_email})
                    </span>
                  )}
                </DialogTitle>
              </DialogHeader>
              <div className="flex-1 overflow-y-auto space-y-3 p-1">
                {selectedConversation.messages?.map((msg, idx) => (
                  <div
                    key={idx}
                    className={`p-3 rounded-lg ${
                      msg.role === "user"
                        ? "bg-blue-100 ml-8"
                        : "bg-pink-100 mr-8"
                    }`}
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`text-xs font-medium ${
                        msg.role === "user" ? "text-blue-700" : "text-pink-700"
                      }`}>
                        {msg.role === "user" ? "👤 Client" : "🌸 Sakura"}
                      </span>
                      {msg.timestamp && (
                        <span className="text-xs text-slate-500">
                          {formatDate(msg.timestamp)}
                        </span>
                      )}
                    </div>
                    <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                  </div>
                ))}
              </div>
              <div className="flex justify-end pt-3 border-t">
                <Button variant="outline" onClick={() => setSelectedConversation(null)}>
                  Fermer
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        )}
      </div>
    </div>
  );
}
